import React, {useContext, useState} from 'react';
import { EledoButton, EledoError, EledoLoader, EledoTabs } from "components/library";
import { useNavigate, useParams} from "react-router-dom";
import useTemplate from 'graphql/hooks/useTemplate';
import { EledoTab } from 'components/library/EledoTabs/EledoTabs';
import EledoWebForm from 'components/library/EledoWebForm/EledoWebForm';
import { RJSFSchema } from '@rjsf/utils';
import generateService from '_api/generate';
import { isNonNullObject } from '@apollo/client/utilities';
import EledoArticleView from 'components/EledoArticles/EledoArticleView';
import classnames from "classnames";
import styles from './EledoCreateRoot.module.scss';
import { PlatformContext } from 'PlatformContext';
import useArticleHistory from 'hooks/useArticleHistory';

const _ = require('lodash');

const EledoCreateRoot = () => {

  const { id } = useParams<{ id: string }>() as { id: string }
  const navigate = useNavigate()
  const { template, loading, error } = useTemplate({id})
  const [formData, setFormData] = useState<any>({})
  const [blobFormData, setBlobFormData] = useState({})
  const [blobData, setBlobData] = useState()
  const [fileName, setFileName] = useState<string>("document.pdf")
  const [generating, setGenerating] = useState<boolean>(false)

  const platform = useContext(PlatformContext)
  const isEmbedded = platform.isEmbedded;
  const history = useArticleHistory(platform.documentationPaths.create)

  const formDataChanged = !_.isEqual(formData, blobFormData)
  const canGenerate = formData && isNonNullObject(formData) && formDataChanged

  const generate = () => {
    setGenerating(true)
    generateService.generateDocument(id, formData)
      .then(response =>{
        setBlobFormData(structuredClone(formData))
        setBlobData(response.data)
        const disposition = response.headers["content-disposition"];
        let filename = "document.pdf"; // fallback

        if (disposition) {
          const match = disposition.match(/filename="?([^"]+)"?/);
          if (match && match[1]) {
            filename = match[1];
          }
        }
        setFileName(filename)
      })
      .finally(() => {
        setGenerating(false)
      })
  }

  if(isEmbedded){
    return (
      <div className={classnames(styles.EledoCreateRoot, "stretch-vertically")} data-testid="EledoCreateRoot">
        <div style={{padding: '20px 30px'}}>
          <EledoArticleView root={platform.documentationPaths.root} history={history}/>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="section title my-title-section stretch-vertically vertical-scroll">
        <div className="container stretch-vertically vertical-scroll">
          <h1 className="page-title">
            {template?.name}
          </h1>
          <p>Fill in the web form and generate your PDF.</p>
          { loading &&
            <div className="editor-container">
              <div>
                <EledoLoader/>
              </div>
            </div>
          }
          { error &&
            <EledoError title="Template was not loaded properly"
              description={error}/>
          }
          { !loading && template && template.type >= 0 && template.derived.reactSchema &&
            <EledoTabs id="spendingTabs">
              <EledoTab key="form" label="Form" path="">
                <div className="stretch-vertically vertical-scroll">
                  <EledoWebForm
                    schema={template.derived.reactSchema as RJSFSchema}
                    formData={formData}
                    onChange={(data) => setFormData(data.formData)}
                    onSubmit={() => navigate("pdf")}
                    />
                </div>

              </EledoTab>
              <EledoTab key="json" label="JSON" path="json">
                  <pre>{JSON.stringify(formData, null, 4)}</pre>

              </EledoTab>
              <EledoTab key="pdf" label="PDF" path="pdf">
                <div className="stretch-vertically vertical-scroll">
                  <div>
                    { blobData &&
                      <EledoButton icon='download2' label='Download' primary={true} onClick={ () => {
                          const url = window.URL.createObjectURL(blobData);
                          const link = document.createElement("a");
                          link.href = url;
                          link.download = fileName; // 👈 forces download instead of opening
                          link.click();

                          URL.revokeObjectURL(url); // cleanup
                      }}/>
                    }
                    <EledoButton icon={blobData ? 'spinner11' : 'rocket'} label={blobData ? 'Re-Generate' : 'Generate'}
                      primary={!blobData}
                      disable={!canGenerate}
                      tooltip={!canGenerate ? (!blobData ? 'Form is empty.' : 'Form data did not change.') : ''}
                      showTooltip={!canGenerate}
                      pendingContent='Generating'
                      pendingState={generating}
                      onClick={ () => generate() }
                    />

                  </div>

                  { !generating && blobData &&
                    <embed src={window.URL.createObjectURL(blobData)} type="application/pdf" width="100%" height="100%"/>
                  }
                </div>

              </EledoTab>
            </EledoTabs>
          }
        </div>
      </div>
    </>

  )
};

export default EledoCreateRoot;
